import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FinanceKpiGrid } from './FinanceKpiGrid';
import { formatDeltaPct, formatTry } from '../../utils/finance/financeFormat';
import { expenseItemBreakdown } from '../../utils/finance/financePnl';
import { fx, fxCardSx } from './financeTheme';

function StatRow({ label, value, hint, tone, testId }) {
  const color = tone === 'warning' ? fx.warning : tone === 'danger' ? fx.danger : tone === 'success' ? fx.success : fx.text;
  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, py: 0.9, borderBottom: `1px solid ${fx.border}`, '&:last-child': { borderBottom: 'none' } }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: fx.text }}>{label}</Typography>
        {hint ? <Typography sx={{ fontSize: 12.5, color: tone === 'warning' ? fx.warning : fx.muted, mt: 0.15 }}>{hint}</Typography> : null}
      </Box>
      <Typography sx={{ fontSize: 14.5, fontWeight: 800, color, whiteSpace: 'nowrap' }}>{value}</Typography>
    </Box>
  );
}

function Panel({ title, action, children }) {
  return (
    <Box sx={{ ...fxCardSx, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ px: 1.75, py: 1.2, borderBottom: `1px solid ${fx.border}`, display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: fx.text }}>{title}</Typography>
        <Box sx={{ flex: 1 }} />
        {action}
      </Box>
      <Box sx={{ px: 1.75, py: 0.4, minHeight: 0, overflow: 'auto' }}>{children}</Box>
    </Box>
  );
}

export default function FinanceOverview({ pnl, delta, giderler, range, onDetail, hasSellTry }) {
  const giderSatirlari = expenseItemBreakdown(giderler, range, pnl.posCommissionTry);
  const missing = pnl.costMissingSalesCount || 0;
  const netLoss = (pnl.netProfitTry || 0) < 0;
  const hasCompare = delta && [delta.revenueTry, delta.expenseTry, delta.netProfitTry].some((v) => v != null);
  const compareBits = [
    delta?.revenueTry != null ? `Ciro ${formatDeltaPct(delta.revenueTry)}` : null,
    delta?.netProfitTry != null ? `Net ${formatDeltaPct(delta.netProfitTry)}` : null,
    delta?.expenseTry != null ? `Gider ${formatDeltaPct(delta.expenseTry)}` : null
  ].filter(Boolean);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 0, flex: 1, overflow: 'hidden' }} data-testid="finance-overview">
      <FinanceKpiGrid pnl={pnl} delta={delta} onDetail={onDetail} hasSellTry={hasSellTry} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1.25,
          minHeight: 0,
          flex: 1
        }}
      >
        <Panel
          title="Dönem Özeti"
          action={(
            <Button size="small" onClick={() => onDetail('kar')} sx={{ textTransform: 'none', fontWeight: 700, color: fx.primary, fontSize: 13 }}>
              Detay Gör
            </Button>
          )}
        >
          {pnl.salesCount === 0 && !pnl.purchaseCount ? (
            <Typography sx={{ py: 1, fontSize: 13, color: fx.muted, fontWeight: 600 }}>Bu dönemde finansal işlem bulunmuyor.</Typography>
          ) : null}
          <StatRow label="Satış sayısı" value={String(pnl.salesCount || 0)} />
          {pnl.purchaseCount ? <StatRow label="Alış sayısı" value={String(pnl.purchaseCount)} /> : null}
          <StatRow label="Ciro" value={formatTry(pnl.revenueTry)} />
          <StatRow label="Maliyet" value={missing ? `${formatTry(pnl.cogsTry)}*` : formatTry(pnl.cogsTry)} />
          <StatRow label="Satış kârı" value={missing ? `${formatTry(pnl.saleProfitTry || 0)}*` : formatTry(pnl.saleProfitTry || 0)} />
          {pnl.purchaseCount ? <StatRow label="Alış kârı" value={formatTry(pnl.purchaseProfitTry || 0)} /> : null}
          <StatRow label="Brüt kâr" value={missing ? `${formatTry(pnl.grossProfitTry)}*` : formatTry(pnl.grossProfitTry)} />
          <StatRow
            testId="cost-coverage"
            label="Maliyet kapsama"
            value={pnl.salesCount === 0 ? '—' : `%${pnl.costCoveragePct}`}
            hint={missing ? `${missing} satışta maliyet bilgisi eksik` : (pnl.salesCount ? 'Dönem satışlarının maliyeti tam' : null)}
            tone={missing ? 'warning' : pnl.salesCount ? 'success' : undefined}
          />
          {giderSatirlari.length > 0 ? (
            <Box sx={{ mt: 0.35, pt: 0.5, borderTop: `1px solid ${fx.border}` }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: fx.muted, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.35 }}>Gider dağılımı</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1.5, rowGap: 0.15 }}>
                {giderSatirlari.map((row) => (
                  <Box key={row.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.75, py: 0.2, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, color: fx.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{formatTry(row.amountTry)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}
        </Panel>
        <Panel title="Finansal Durum">
          <StatRow label="Toplam gider" value={formatTry(pnl.expenseTry)} />
          {pnl.posCommissionTry ? (
            <StatRow
              label="— POS komisyonu"
              value={formatTry(pnl.posCommissionTry)}
              hint="Kredi kartı tahsilatından banka kesintisi"
            />
          ) : null}
          <StatRow
            label={netLoss ? 'Net zarar' : 'Net kâr'}
            value={missing ? `${formatTry(Math.abs(pnl.netProfitTry || 0))}*` : formatTry(Math.abs(pnl.netProfitTry || 0))}
            tone={netLoss ? 'danger' : 'success'}
          />
          <StatRow
            label="Net kâr marjı"
            value={pnl.netMarginPct == null ? '—' : `%${Number(pnl.netMarginPct).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          />
          <StatRow
            label="Önceki döneme göre"
            value={hasCompare ? (compareBits[0] || '—') : '—'}
            hint={hasCompare ? compareBits.slice(1).join(' · ') : 'Karşılaştırılacak önceki dönem verisi yok'}
          />
          {missing ? (
            <Box sx={{ mt: 0.5, mb: 0.75, px: 1.1, py: 0.85, borderRadius: '8px', bgcolor: fx.warningSoft }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: fx.warning }}>
                {missing} satışta maliyet bilgisi eksik
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: fx.warning, mt: 0.15 }}>
                Maliyet kapsama %{pnl.costCoveragePct} — kâr rakamları tam olmayabilir.
              </Typography>
            </Box>
          ) : null}
        </Panel>
      </Box>
    </Box>
  );
}
